'use client';
import React, { useEffect, useState } from 'react';

import { AnimatedNumber } from '~/components/animated-number';
import { Badge } from '~/components/badge';
import { GridLine } from '~/components/grid-line';
import { HoverCard } from '~/components/hover-card';
import { CardBody, CardContainer, CardItem } from '~/components/ui/3d-card';
import { cn } from '~/lib/utils';

import { AreaChart, BarChart } from '@tremor/react';

export const ProvenReliabilitySkeleton = () => {
  return (
    <CardContainer containerClassName="inter-var w-full" className="w-full" perspective="2000px">
      <div className="absolute h-20 bottom-0 inset-x-0 bg-gray-100 dark:bg-zinc-800 z-20 [mask-image:linear-gradient(to_bottom,transparent,white)]" />
      <CardBody className="relative group/card w-full h-full rounded-xl p-6   bg-white  dark:bg-zinc-700   dark:hover:text-primary-600 dark:hover:ring-primary-600 ">
        <CardItem className="h-full w-full">
          {/* <Graph /> */}
          <BarGraph />
        </CardItem>
      </CardBody>
    </CardContainer>
  );
};

const BarGraph = () => {
  const chartdata2 = [
    {
      name: '1 Sec Cutoff',
      'vLLM Quant': 1,
      'vLLM FP16': 8,
      'Fireworks FP16': 22,
      'Fireworks FP8': 74,
    },
    {
      name: '2 Sec Cutoff',
      'vLLM Quant': 1,
      'vLLM FP16': 12,
      'Fireworks FP16': 38,
      'Fireworks FP8': 80,
    },
    {
      name: '3 Sec Cutoff',
      'vLLM Quant': 10,
      'vLLM FP16': 18,
      'Fireworks FP16': 40,
      'Fireworks FP8': 80,
    },
  ];

  const valueFormatter = (number: number) => `${number} req/sec`;

  return (
    <BarChart
      className="h-60 mt-4"
      data={chartdata2}
      index="name"
      showTooltip={false}
      categories={['vLLM Quant', 'vLLM FP16', 'Fireworks FP16', 'Fireworks FP8']}
      valueFormatter={valueFormatter}
      showYAxis={true}
      colors={['yellow', 'teal', 'purple', 'violet']}
      yAxisWidth={80}
    />
  );
};

export const Graph = () => {
  const chartdata2 = [
    {
      date: 'Jan 1',
      latency: 0,
      throughput: 10,
    },
    {
      date: 'Jan 2',
      latency: 10,
      throughput: 40,
    },
    {
      date: 'Jan 3',
      latency: 20,
      throughput: 90,
    },
    {
      date: 'Jan 4',
      latency: 10,
      throughput: 60,
    },
    {
      date: 'Jan 5',
      latency: 40,
      throughput: 100,
    },
    {
      date: 'Jan 6',
      latency: 50,
      throughput: 140,
    },
    {
      date: 'Jan 7',
      latency: 20,
      throughput: 80,
    },
    {
      date: 'Jan 8',
      latency: 10,
      throughput: 160,
    },
    {
      date: 'Jan 9',
      latency: 40,
      throughput: 180,
    },
    {
      date: 'Jan 10',
      latency: 20,
      throughput: 200,
    },
    {
      date: 'Jan 11',
      latency: 30,
      throughput: 155,
    },
    {
      date: 'Jan 12',
      latency: 40,
      throughput: 160,
    },
  ];

  const valueFormatter = (v: any) => {
    return v;
  };

  return (
    <AreaChart
      className="h-60 mt-4"
      data={chartdata2}
      index="date"
      categories={['latency', 'throughput']}
      colors={['emerald', 'indigo']}
      yAxisWidth={30}
      connectNulls={true}
      style={{ borderRadius: '10px' }}
      valueFormatter={valueFormatter}
      showAnimation={true}
      animationDuration={2000}
      showGridLines={false}
      curveType="monotone"
    />
  );
};

export const BlazingFastInferenceSkeleton = () => {
  const [value, setValue] = useState({
    first: 0,
    second: 0,
    third: 0,
  });
  useEffect(() => {
    setValue({
      first: 200,
      second: 110,
      third: 67,
    });
  }, []);
  return (
    <CardContainer containerClassName="inter-var w-full  relative" className="w-full">
      <div className="absolute h-20 bottom-0 inset-x-0 bg-gray-100 dark:bg-zinc-800 z-20 [mask-image:linear-gradient(to_bottom,transparent,white)]" />
      <CardBody className="relative group/card w-full h-full rounded-xl p-6   bg-white  dark:bg-zinc-700  dark:hover:text-primary-600 dark:hover:ring-primary-600 ">
        <CardItem translateZ={100} className="py-4 flex items-end space-x-1">
          <AnimatedNumber className="text-6xl font-bold text-primary-400 min-w-[116px]" value={value.first} />{' '}
          <div className="text-sm text-neutral-600 dark:text-neutral-300 flex flex-col">
            <Badge>Fireworks AI</Badge>
            <span>tokens / second</span>
          </div>
        </CardItem>
        <GridLine />

        <CardItem translateZ={50} className="py-4 flex items-end space-x-1">
          <AnimatedNumber className="text-5xl font-normal text-neutral-500 min-w-[84px]" value={value.second} />{' '}
          <div className="text-sm text-neutral-600 dark:text-neutral-300 flex flex-col">
            <Badge className="bg-gray-200 text-gray-500 dark:bg-zinc-800 dark:text-zinc-300">Next provider</Badge>
            <span>tokens / second</span>
          </div>
        </CardItem>
        <GridLine />
        <CardItem translateZ={20} className="py-4 flex items-end space-x-1">
          <AnimatedNumber className="text-5xl font-normal text-neutral-500 min-w-[64px]" value={value.third} />{' '}
          <div className="text-sm text-neutral-600 dark:text-neutral-300 flex flex-col">
            <Badge className="bg-gray-200 text-gray-500 dark:bg-zinc-800 dark:text-zinc-300">average provider</Badge>
            <span>tokens / second</span>
          </div>
        </CardItem>
      </CardBody>
    </CardContainer>
  );
};

export const TunedForUseCaseSkeleton = () => {
  const [value, setValue] = useState({
    first: 0,
    second: 0,
  });
  useEffect(() => {
    setValue({
      first: 25,
      second: 99.99,
    });
  }, []);
  return (
    <HoverCard className="relative h-60 overflow-hidden">
      <div className="absolute  h-20 bottom-0 pointer-events-none inset-x-0 bg-gray-100 dark:bg-zinc-800 z-50 [mask-image:linear-gradient(to_bottom,transparent,white)]" />
      <div
        className={cn(
          'animate-fade-in rounded-3xl flex flex-col items-center justify-center space-y-4 bg-white text-black dark:bg-zinc-700 h-full overflow-hidden p-6 opacity-0 shadow-md shadow-gray-900/5 relative',
        )}
      >
        <div className="absolute inset-0 bg-grid-small-zinc-200 dark:bg-grid-small-zinc-600" />
        <div className="flex flex-col w-full py-2 items-center relative z-20 shadow-aceternity dark:bg-zinc-700 bg-gray-50 rounded-xl">
          <div>
            <AnimatedNumber className="text-3xl font-bold text-primary-200 dark:text-primary-500" value={value.first} />{' '}
            <span className="dark:text-neutral-300">Billion+</span>
          </div>
          <span className="dark:text-neutral-300">tokens served in a day</span>
        </div>
      </div>
      {/* <figure
        className={cn(
          "animate-fade-in rounded-3xl relative bg-white text-black dark:bg-zinc-700 h-full overflow-hidden p-6 opacity-0 shadow-md shadow-gray-900/5"
        )}
      >
        <blockquote className="text-gray-900 dark:text-zinc-200">
          <p className="mt-3 text-[12px]">
            &quot;Fireworks enabled us to deliver industry-leading speed for
            both LLM and image generation inference. It makes a huge difference
            for the customer experience when the AI responses are fast and
            interactive.&quot;
          </p>
        </blockquote>
        <figcaption className="mt-3 text-[12px] text-primary-400 font-bold dark:text-primary-500">
          {`Ves Stoyanov`}
        </figcaption>
        <div className="mt-1 text-[12px] text-gray-600 dark:text-zinc-400">
          {`Head of AI, Tome`}
        </div>
      </figure> */}
    </HoverCard>
  );
};
